import axios from 'axios';
import Config from '../../../../config';
import { loadProfiles as loadExportProfiles } from '../../../files/export';
import { API_ENDPOINTS, FILE_TYPES, EXPORT_PROFILES } from '../constants';
import { ExportProfile, ExportResult } from '../types';

class ExportService {
  profilesCache: ExportProfile[] | null;

  constructor() {
    this.profilesCache = null;
  }

  /**
   * Load export profiles for orders
   * @param signal - Optional abort signal for cancellation
   * @returns Export profiles
   */
  async loadProfiles(signal?: AbortSignal): Promise<ExportProfile[]> {
    try {
      if (this.profilesCache) {
        return this.profilesCache;
      }

      const profiles = await loadExportProfiles() as ExportProfile[];
      const orderProfiles = profiles.filter(p => p.DocumentType === 'Order');

      // Prioritize common profiles
      const priorityProfiles = orderProfiles.filter(p =>
        p.Name === EXPORT_PROFILES.WITH_DETAIL ||
        p.Name === EXPORT_PROFILES.WITHOUT_DETAIL
      );
      const otherProfiles = orderProfiles.filter(p =>
        p.Name !== EXPORT_PROFILES.WITH_DETAIL &&
        p.Name !== EXPORT_PROFILES.WITHOUT_DETAIL
      );

      this.profilesCache = [...priorityProfiles, ...otherProfiles];
      return this.profilesCache;
    } catch (error) {
      console.error('Error loading export profiles:', error);
      throw error;
    }
  }

  /**
   * Export orders with specified profile and file type
   * @param orderIds - Order IDs to export
   * @param profile - Export profile
   * @param fileType - File type (csv, xlsx, pdf, xml)
   * @param signal - Optional abort signal for cancellation
   * @returns Export URL and filename
   */
  async exportOrders(orderIds: Array<string | number>, profile: ExportProfile, fileType: string = FILE_TYPES.CSV, signal?: AbortSignal): Promise<ExportResult> {
    try {
      const fields = profile.Fields
        .filter(f => f.field)
        .map(f => f.alias ? `${f.field} ${f.alias}` : f.field)
        .join(',');

      const actualFileType = fileType === FILE_TYPES.LEGACY ? FILE_TYPES.XML : fileType;

      // axios@0.19 predates AbortSignal support; passed through as a loose config
      // extra to preserve the (already no-op) original behavior under its types.
      const axiosConfig: Record<string, any> = { signal };
      const response = await axios.get(
        `${Config.api}${API_ENDPOINTS.ORDERS_EXPORT}?` +
        `LogicbrokerKeys=${orderIds.join(',')}&` +
        `fields=${fields}&` +
        `FileType=${actualFileType}`,
        axiosConfig
      );

      return {
        href: response.data.Body,
        fileName: `orders-${Math.floor(Date.now() / 1000)}.${actualFileType}`
      };
    } catch (error) {
      console.error('Error exporting orders:', error);
      throw error;
    }
  }

  /**
   * Download packing slips for selected orders
   * @param orderIds - Order IDs
   * @param signal - Optional abort signal for cancellation
   * @returns Download URL and filename
   */
  async downloadPackingSlips(orderIds: Array<string | number>, signal?: AbortSignal): Promise<ExportResult> {
    try {
      const axiosConfig: Record<string, any> = { signal };
      const response = await axios.get(
        `${Config.api}${API_ENDPOINTS.PACKING_LIST}?` +
        `fileType=${FILE_TYPES.PDF}&` +
        `LogicbrokerKeys=${orderIds.join(',')}`,
        axiosConfig
      );

      return {
        href: response.data.Body,
        fileName: `ordersPackingSlip-${Math.floor(Date.now() / 1000)}.${FILE_TYPES.PDF}`
      };
    } catch (error) {
      console.error('Error downloading packing slips:', error);
      throw error;
    }
  }

  /**
   * Clear cached profiles
   */
  clearCache(): void {
    this.profilesCache = null;
  }
}

const exportService = new ExportService();
export default exportService;